import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  PencilIcon, 
  EyeIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const StudentList = ({ students, loading, error, onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(searchTerm);
  };


  const handleViewStudent = (studentId) => {
    navigate(`/staff/students/${studentId}`);
  };

  const handleEditStudent = (studentId) => {
    navigate(`/staff/students/${studentId}/edit`);
  };

  const getStatusBadge = (isActive) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    if (isActive) {
      return <span className={`${baseClasses} bg-green-100 text-green-800`}>Active</span>;
    } else {
      return <span className={`${baseClasses} bg-red-100 text-red-800`}>Inactive</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSearch} className="flex space-x-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name, email, or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Search
          </button>
        </form>
      </div>

      {/* Students Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Students ({students.length})
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Manage student information and assignments
          </p>
        </div>
        
        {students.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No students found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new student.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {students.map((student) => (
              <li key={student.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-indigo-600">
                          {student.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{student.name}</p>
                        <span className="ml-2">{getStatusBadge(student.is_active)}</span>
                      </div>
                      <div className="mt-1">
                        <p className="text-sm text-gray-500">{student.email}</p>
                        <p className="text-sm text-gray-500">{student.phone_no}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {student.lesson_packs && student.lesson_packs.length > 0 ? (
                          <div className="space-y-1">
                            {student.lesson_packs.slice(0, 2).map((pack, index) => (
                              <div key={pack.id} className="flex items-center justify-end">
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full mr-2">
                                  {pack.subject}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {pack.no_of_hours}h
                                </span>
                              </div>
                            ))}
                            {student.lesson_packs.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{student.lesson_packs.length - 2} more
                              </div>
                            )}
                          </div>
                        ) : student.lesson_pack ? (
                          <div>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full mr-2">
                              {student.lesson_pack.subject}
                            </span>
                            <span className="text-xs text-gray-500">
                              {student.lesson_pack.no_of_hours}h
                            </span>
                          </div>
                        ) : (
                          'No lesson packs'
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Status: {student.is_active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewStudent(student.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="View Details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEditStudent(student.id)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Edit Student"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

    </div>
  );
};

export default StudentList;
